import { eventChannel } from "redux-saga";
import { cancel, fork, put, take, takeEvery } from "redux-saga/effects";
import { captureException } from "@sentry/react";

import type { TheirStorySdk } from "../../inheritFromWeeb-ui-v2/state/gqlClient";
import type { WatchStorySubscription } from "../../inheritFromWeeb-ui-v2/generated/graphql";
import { getTheirStorySdkSaga } from "../../inheritFromWeeb-ui-v2/state/gqlClient";
import {
  CALL_CLEAR,
  CALL_FINISH,
  CALL_PARTICIPANT_STORAGE_QUOTA_DETERMINED,
  CALL_STARTED,
  CALL_STATE_UPDATED,
  STOP_WATCHING_CALL_STATE,
  WATCH_CALL_STATE,
} from "../../../actions/types";
import { sleep } from "../../../../components/utils/util";

function calculateExponentialBackoff(count: number, limit = 30000) {
  return Math.min(0.5 * 1.5 ** count - 0.5, limit / 1000) * 1000;
}

function* watchStorageQuota(delay = 10000) {
  if (!window?.navigator?.storage?.estimate) {
    return;
  }
  try {
    let timesUnchanged = 0;
    let previousStorageEstimate: StorageEstimate;
    while (true) {
      const quota: StorageEstimate = yield window.navigator.storage.estimate();
      // If quota values don't change, don't report no-op commands and slow down sampling
      if (previousStorageEstimate?.quota === quota?.quota && previousStorageEstimate?.usage === quota?.usage) {
        timesUnchanged += 1;
        yield sleep(calculateExponentialBackoff(timesUnchanged + 2, 60000));
        continue;
      }
      timesUnchanged = 0;
      yield put({
        type: CALL_PARTICIPANT_STORAGE_QUOTA_DETERMINED,
        data: quota, // Should be `payload`, but everything else uses `data` so... :(
      });
      yield sleep(delay);
    }
  } catch (e) {
    // Don't re-throw because we want this to soft-fail
    console.warn("Unable to determine quota: ", e);
    captureException(e);
  }
}

export function* watchLocalRecordingCallState() {
  const watching: Record<string, boolean> = {};
  yield takeEvery([CALL_STARTED, WATCH_CALL_STATE], function* (action: { type: string; data: { storyId: string } }) {
    if (watching[action.data.storyId]) {
      console.log("Already watching story for changes", action.data.storyId);
      return;
    } else {
      console.log("Watching story for changes", action.data.storyId);
    }
    watching[action.data.storyId] = true;
    try {
      const watchProc = yield fork(watchStorageQuota);
      const sdk: TheirStorySdk = yield getTheirStorySdkSaga();

      const observable = sdk.watchStory({ storyId: action.data.storyId });

      const channel = eventChannel<WatchStorySubscription>((callback) => {
        // TODO: In saga docs eventChannel 'callback' function is used to emit event payloads as well as any error
        // Saga will internally convert error instances into exceptions and throw those.
        // Observable has 3 callbacks: next, error and complete. We are only using one of them, next. Therefore
        // any errors return here end up being captured by global unhandled exception handler. Is that good?
        // How do we recover from them?
        const subscription = observable.subscribe(callback);
        return subscription.unsubscribe.bind(subscription);
      });

      yield takeEvery(channel, function* (state) {
        const aggregate = state.watchStoryState.data.aggregate;
        console.log("Got story state", aggregate);
        yield put({ type: CALL_STATE_UPDATED, data: { stream: action.data, callState: aggregate } });
      });

      if (action.type === CALL_STARTED) {
        yield take([CALL_FINISH, CALL_CLEAR]);
      } else {
        yield take(
          (potentialCancelAction) =>
            potentialCancelAction.type === STOP_WATCHING_CALL_STATE &&
            potentialCancelAction.data.storyId === action.data.storyId
        );
      }

      channel.close();
      yield cancel(watchProc);
    } finally {
      console.log("Stopped watching", action.data.storyId);
      watching[action.data.storyId] = false;
    }
  });
}
